import { Component, type ErrorInfo, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { PostHogProvider } from "posthog-js/react";
import { queryClient } from "@/shared/lib/query-client";
import { initPostHog, posthog } from "@/shared/lib/posthog";
import { initSentry, captureException } from "@/shared/lib/sentry";
import i18n from "./i18n/index";
import App from "./App.tsx";
import "./index.css";

// Crash reporting must be live regardless of the PostHog cookie-consent gate.
try {
  initSentry();
} catch (err) {
  console.error("[Sentry] init failed:", err);
}

// Apply the saved theme before first paint. The toggle now lives on the
// Settings page (and useTheme only touches the DOM when you flip it), so
// without this the stored preference is dropped on every reload.
try {
  document.documentElement.classList.toggle(
    "dark",
    localStorage.getItem("ubik-theme") === "dark",
  );
} catch {
  // localStorage can throw in a blocked-cookie context — light mode is fine.
}

class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; eventId: string | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, eventId: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    // Send the stack to Sentry — never to the screen. Keep the returned event id
    // so the user can quote it to support.
    const eventId = captureException(error, { tags: { component: "frontend" } });
    this.setState({ eventId: eventId || null });
  }

  render() {
    if (this.state.hasError) {
      const { eventId } = this.state;
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-3 bg-background p-6 text-center text-foreground">
          <h1 className="text-lg font-semibold">{i18n.t("errors:something_went_wrong", "Something went wrong")}</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            {i18n.t("errors:boundary_desc", "An unexpected error occurred. Please reload the page. If it keeps happening, share the reference below with support.")}
          </p>
          {eventId && (
            <button
              type="button"
              className="rounded-md border bg-muted px-3 py-1.5 text-xs"
              title={i18n.t("errors:copy_reference", "Click to copy this reference")}
              onClick={() => {
                void navigator.clipboard?.writeText(eventId);
              }}
            >
              {i18n.t("errors:reference_click", { id: eventId, defaultValue: "Reference: {{id}} (click to copy)" })}
            </button>
          )}
          <button
            className="rounded-md border px-3 py-1.5 text-sm"
            onClick={() => {
              this.setState({ hasError: false, eventId: null });
              window.location.reload();
            }}
          >
            {i18n.t("errors:reload", "Reload")}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

try {
  initPostHog();
} catch (err) {
  console.error("[PostHog] init failed:", err);
}

// Offline tolerance (WF10): cache-first hashed assets + a cached shell for
// offline launches. Prod builds only — a SW on the dev server serves stale
// modules and makes HMR lie. See public/sw.js for what it deliberately
// never touches (/api, /oauth, /ws).
// Inside the Capacitor shell: restore the stored session token and listen
// for the ubikcapture:// login deep link. No-op on the web (dynamic imports,
// nothing bundled into the entry).
try {
  if (window.Capacitor?.isNativePlatform?.()) {
    void import("@/features/capture/native-bridge").then((bridge) => bridge.initNativeBridge());
  }
} catch {
  /* not the app */
}

// Never inside the Capacitor shells: the bundle is local, and WKWebView has no
// service workers on the capacitor:// scheme anyway — be explicit, not lucky.
if (import.meta.env.PROD && "serviceWorker" in navigator && !window.Capacitor?.isNativePlatform?.()) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      console.error("[SW] register failed:", err);
    });
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    captureException(event.error instanceof Error ? event.error : event.message, {
      tags: { component: "frontend" },
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    captureException(
      event.reason instanceof Error ? event.reason : String(event.reason),
      { tags: { component: "frontend" } },
    );
  });
}

createRoot(document.getElementById("root")!).render(
  <AppErrorBoundary>
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </PostHogProvider>
  </AppErrorBoundary>,
);
